import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { auth } from './firebase';
import config from './config';
import { markTourOfferPending } from './GuidedTour';
import logo from './vica.png';

const AUTH_TIMEOUT_MS = 15000;
const USER_ID_TIMEOUT_MS = 12000;

// Plain-language messages for sign-in failures, instead of Firebase's raw
// "Firebase: Error (auth/...)" text.
const SIGN_IN_ERROR_MESSAGES = {
  'auth/invalid-credential': "The email and password don't match. Please try again.",
  'auth/invalid-login-credentials': "The email and password don't match. Please try again.",
  'auth/wrong-password': "The email and password don't match. Please try again.",
  'auth/user-not-found': "The email and password don't match. Please try again.",
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/missing-password': 'Please enter your password.',
  'auth/too-many-requests':
    'Too many attempts. Please wait a few minutes and try again, or use "Forgot password?".',
  'auth/user-disabled': 'This account has been turned off. Please contact the study team.',
  'auth/network-request-failed':
    'We could not connect. Please check your internet connection and try again.',
};

const getSignInErrorMessage = (err) => {
  if (err?.code && SIGN_IN_ERROR_MESSAGES[err.code]) {
    return SIGN_IN_ERROR_MESSAGES[err.code];
  }
  // Errors we raise ourselves (e.g. the timeout) already read well; anything
  // else from Firebase gets a generic message.
  if (!err?.code && err?.message && !err.message.startsWith('Firebase')) {
    return err.message;
  }
  return 'We could not sign you in. Please try again.';
};

const withTimeout = (promise, timeoutMessage, timeoutMs = AUTH_TIMEOUT_MS) =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
    }),
  ]);

// `onEnrolled` tells App that the backend enrollment id is now in localStorage.
function Login({ onEnrolled }) {
  const [isResetView, setIsResetView] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const showResetView = () => {
    setIsResetView(true);
    setError('');
    setInfoMessage('');
    setPassword('');
  };

  const showLoginView = () => {
    setIsResetView(false);
    setError('');
    setInfoMessage('');
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setInfoMessage('');
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setError('Please enter your email address.');
      setIsSubmitting(false);
      return;
    }

    try {
      await withTimeout(
        sendPasswordResetEmail(auth, normalizedEmail),
        'Sending the reset email is taking too long. Please try again.'
      );
    } catch (err) {
      // Avoid leaking which emails are registered: only surface input/rate errors.
      if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
        setIsSubmitting(false);
        return;
      }
      if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a moment and try again.');
        setIsSubmitting(false);
        return;
      }
      console.error('Password reset error:', err);
    }

    setInfoMessage(
      'If an account exists for that email, a password reset link has been sent. Please check your inbox and spam folder.'
    );
    setIsSubmitting(false);
  };

  const fetchAndStoreBackendUserId = async (userEmail) => {
    const response = await withTimeout(
      axios.get(`${config.API_BASE_URL}/login`, {
        params: { email: userEmail },
      }),
      'Loading your sessions is taking too long. Please try again.',
      USER_ID_TIMEOUT_MS
    );

    const userId = response?.data?.id ? String(response.data.id) : '';
    if (!userId) {
      throw new Error('Unable to load your account profile.');
    }

    localStorage.setItem('userId', userId);
    return userId;
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    const normalizedEmail = email.trim().toLowerCase();

    localStorage.setItem('authEmail', normalizedEmail);
    localStorage.removeItem('userId');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('sessionIsNew');
    // Clear any stale enrollment id in App so a Firebase sign-in alone can't
    // count as authenticated before the backend check below completes.
    if (typeof onEnrolled === 'function') {
      onEnrolled();
    }

    try {
      const userCredential = await withTimeout(
        signInWithEmailAndPassword(auth, normalizedEmail, password),
        'Login is taking too long. Please try again.'
      );
      localStorage.setItem('firebaseUid', userCredential.user.uid);

      // A Firebase account alone is not enough: the study team must also have
      // enrolled this email. Never let a half-provisioned account reach the app.
      try {
        await fetchAndStoreBackendUserId(normalizedEmail);
      } catch (backendError) {
        await signOut(auth).catch(() => {});
        localStorage.removeItem('firebaseUid');
        localStorage.removeItem('authEmail');

        if (backendError.response?.status === 403) {
          setError(
            backendError.response.data?.error
              || 'This account is not enrolled in the study. Please contact the study team.'
          );
        } else {
          console.error('Backend login error:', backendError);
          setError('We could not reach the study server. Please try again in a moment.');
        }
        return;
      }

      if (typeof onEnrolled === 'function') {
        onEnrolled();
      }
      // Offer the guided tour once on Home for this login.
      markTourOfferPending();
      navigate('/home', { replace: true });
    } catch (err) {
      if (err?.code) {
        console.warn('Sign-in failed:', err.code);
      }
      setError(getSignInErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vica-page-gradient vica-safe-viewport flex items-center justify-center px-4">
      <div className="vica-surface w-full max-w-md p-8">
        <div className="flex justify-center mb-4">
          <img src={logo} alt="VICA" className="h-14 w-auto" />
        </div>

        <h1 className="vica-title text-center">
          {isResetView ? 'Reset Password' : 'Welcome Back'}
        </h1>
        <p className="vica-subtitle text-center">
          {isResetView
            ? 'Enter your email and we will send you a reset link.'
            : 'Sign in with the email and password provided by the study team.'}
        </p>

        {isResetView ? (
          <form className="space-y-4 mt-6" onSubmit={handlePasswordReset}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="vica-input"
              required
            />

            <button type="submit" className="vica-btn vica-btn-lg w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Please wait...' : 'Send reset link'}
            </button>

            {infoMessage && (
              <p className="text-green-700 text-center text-sm font-semibold">{infoMessage}</p>
            )}
            {error && <p className="text-red-700 text-center text-sm font-semibold">{error}</p>}

            <button
              type="button"
              className="vica-link w-full text-center text-sm font-semibold text-[#006298]"
              onClick={showLoginView}
            >
              Back to login
            </button>
          </form>
        ) : (
          <>
            <form className="space-y-4 mt-6" onSubmit={handleAuth}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="vica-input"
                required
              />

              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="vica-input"
                required
              />

              <button type="submit" className="vica-btn vica-btn-lg w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Please wait...' : 'Login'}
              </button>

              {error && <p className="text-red-700 text-center text-sm font-semibold">{error}</p>}
            </form>

            <button
              type="button"
              className="vica-link mt-4 w-full text-center text-sm font-semibold text-[#006298]"
              onClick={showResetView}
            >
              Forgot password?
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default Login;
